<script setup lang="ts">
/**
 * The Keepsake agreement with its acceptance controls, shared by the setup
 * flow and the app-wide wall shown when the agreement changes.
 *
 * The button stays locked until the reader has scrolled to the end. All the
 * legal terms, including what clicking it means, are in the agreement text.
 * The parent records the acceptance.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { marked } from 'marked'
import type { CurrentEula } from '@/api/setup'

const props = defineProps<{
  eula: CurrentEula
  submitting: boolean
}>()

const emit = defineEmits<{ accept: [] }>()

const scroller = ref<HTMLElement | null>(null)
const readToEnd = ref(false)
const scrollPercent = ref(0)

const renderedHtml = computed(() => marked.parse(props.eula.markdown, { async: false }) as string)

function onScroll() {
  const el = scroller.value
  if (!el) return
  const max = el.scrollHeight - el.clientHeight
  scrollPercent.value = max <= 0 ? 100 : Math.min(100, Math.round((el.scrollTop / max) * 100))
  if (max <= 0 || el.scrollTop >= max - 8) readToEnd.value = true
}

// A different text means reading starts over.
watch(renderedHtml, async () => {
  readToEnd.value = false
  scrollPercent.value = 0
  await nextTick()
  onScroll()
})

onMounted(async () => {
  window.addEventListener('resize', onScroll)
  await nextTick()
  onScroll()
})
onBeforeUnmount(() => window.removeEventListener('resize', onScroll))
</script>

<template>
  <div>
    <div
      ref="scroller"
      @scroll.passive="onScroll"
      class="border border-gray-200 rounded-lg p-4 mb-2 h-[55vh] overflow-y-auto prose prose-sm prose-gray max-w-none bg-white"
      v-html="renderedHtml"
    />

    <div class="mb-4">
      <div class="h-1 bg-gray-100 rounded overflow-hidden">
        <div
          class="h-full transition-all"
          :class="readToEnd ? 'bg-green-600' : 'bg-gray-400'"
          :style="{ width: (readToEnd ? 100 : scrollPercent) + '%' }"
        />
      </div>
      <p class="text-xs mt-1" :class="readToEnd ? 'text-green-700' : 'text-gray-500'">
        {{ readToEnd ? 'You have reached the end of the agreement.' : 'Scroll to the end of the agreement to continue.' }}
      </p>
    </div>

    <button
      @click="emit('accept')"
      :disabled="!readToEnd || submitting"
      class="w-full bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition disabled:opacity-50 text-lg"
    >
      {{ submitting ? 'Recording…' : 'I agree' }}
    </button>
  </div>
</template>
